import React from 'react';

import i18n from 'config/i18n';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField
} from 'components/shared';
import { ControlledColorPickerPopover } from 'components/shared/ControlledFields/ControlledColorPickerPopover';
import { ControlledFlagCheckbox } from 'components/shared/ControlledFields/ControlledFlagCheckbox';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { Skeleton } from '@mui/lab';
import { useGetDccOptions } from './TimeBoundValuesSection/hooks/useGetDccOptions';
import Box from '@mui/material/Box';

export const defaultSectionSchema = Yup.object().shape({
  dutyTypeFlags: Yup.array().default([]),
  valid: Yup.object({
    from: Yup.date()
      .required(
        i18n.t('validationFieldRequired', { name: i18n.t('validFrom') })
      )
      .nullable()
      .default(null),
    to: Yup.date()
      .required(i18n.t('validationFieldRequired', { name: i18n.t('validTo') }))
      .nullable()
      .default(null)
      .min(Yup.ref<Date>('from'), i18n.t('invalidInterval'))
  })
});

export const DefaultSection: React.FC = () => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const getDccOptions = useGetDccOptions();

  return (
    <MultiStepForm.Section
      title={t('basis')}
      validateFields={['code, description, valid.from', 'valid.to', 'dccSvc']}
    >
      <ControlledTextField
        name="code"
        control={formContext.control}
        label={t('code')}
        variant="outlined"
      />
      <ControlledTextField
        name="description"
        control={formContext.control}
        label={t('description')}
        variant="outlined"
      />
      {getDccOptions.isLoading ? (
        <Skeleton />
      ) : (
        <ControlledSelect
          name="dccSvc"
          label={t('dutyHourTypes.fieldName.dutyCalculationCode')}
          control={formContext.control}
          options={getDccOptions.data ?? []}
          variant="outlined"
        />
      )}
      <Box display="flex" gap={2}>
        <ControlledDatePicker
          variant="outlined"
          name="valid.from"
          control={formContext.control}
          label={t('validFrom')}
          fullWidth
        />
        <ControlledDatePicker
          variant="outlined"
          name="valid.to"
          control={formContext.control}
          label={t('validTo')}
          fullWidth
        />
      </Box>
      <ControlledFlagCheckbox
        name="dutyTypeFlags"
        flag="DEFAULT"
        label={t('dutyHourTypes.fieldName.defaultDht')}
      />
      <ControlledColorPickerPopover name="colorCode" label={t('color')} />
    </MultiStepForm.Section>
  );
};
